/**
 * Единая модель отображения команды (KAN-814).
 *
 * До этого команду рисовали три независимых компонента: панель в профиле,
 * плитка каталога `/rosters` и карточка объявления на доске `/lfg`. Так как
 * вкладка LFT становится списком команд, ищущих игрока, одна и та же команда
 * оказывается сразу на двух страницах — и должна выглядеть одинаково.
 *
 * Здесь описан нейтральный вид команды: он не знает ни про DTO бэкенда, ни про
 * страницу. Каждый источник данных приводится к нему адаптером (`./adapters`).
 */

/** Пять игровых линий — костяк состава. Порядок фиксирован: так читают состав. */
export const ROSTER_LANES = ["top", "jungle", "mid", "adc", "support"] as const;

export type RosterLane = (typeof ROSTER_LANES)[number];

/** Состояние слота на доске поиска. Там, где доски нет, статуса тоже нет. */
export type RosterSlotStatus = "vacant" | "seeking" | "filled";

/** Человек в составе. Пустых полей быть не должно: их не рисуют, а опускают. */
export interface RosterPersonView {
  id?: string | null;
  /**
   * Имя. У состава команды всегда непустое: безымянный участник — плейсхолдер,
   * он в модель не попадает. Пустым бывает только у слота доски, про который
   * известен аватар, но не ник — тогда имя просто не рисуют.
   */
  displayName: string;
  avatarUrl?: string | null;
  /** Иконка профиля Riot — запасной аватар, если своего нет. */
  riotProfileIconId?: number | null;
  /** Подпись роли для участников вне пяти линий (запас, тренер). */
  roleLabel?: string | null;
  opggUrl?: string | null;
  /** Ник на платформе — по нему открывается страница игрока. */
  username?: string | null;
}

/** Слот линии: сама линия + кто на ней стоит (или никто). */
export interface RosterLaneSlot {
  lane: RosterLane;
  member?: RosterPersonView | null;
  /**
   * Подписи слота: роль, ранг, регион, статус в команде. Приходят от источника
   * уже очищенными от прочерков — свободный слот показывает только роль.
   */
  badges?: string[];
  status?: RosterSlotStatus | null;
  /** Локализованная подпись статуса. Нет подписи — нет и бейджа статуса. */
  statusLabel?: string | null;
}

export interface RosterCardData {
  id: string;
  name: string;
  tag?: string | null;
  logoUrl?: string | null;
  /** Обложка для шапки плитки. У варианта `board` шапки-картинки нет. */
  bannerUrl?: string | null;
  isVerified?: boolean;
  isLookingForPlayers?: boolean;
  /** Средний ранг команды, как его посчитал бэкенд (напр. «PLATINUM II»). */
  averageRank?: string | null;
  /** Сколько людей в команде. null — если источник этого не знает. */
  memberCount?: number | null;
  /** Куда ведёт клик по карточке. Нет ссылки — карточка не кликабельна. */
  href?: string | null;
  lanes: RosterLaneSlot[];
  /** Состав вне пяти линий: запас, тренер, команды без ролей (турнирные). */
  extras: RosterPersonView[];
}
